import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Loader2, Search } from "lucide-react";
import api, { getApiErrorMessage } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { formatAuditAction, formatAuditEntity, formatUserType } from "../../lib/display";

const formatDateTime = (value) => {
  if (!value) return "Não informado";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const AdminAuditoria = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchLogs = async () => {
    try {
      const response = await api.get("/auditoria?limit=150");
      setLogs(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar auditoria."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    const normalizedSearch = search.toLowerCase();
    return logs.filter((log) => {
      return (
        log.description?.toLowerCase().includes(normalizedSearch) ||
        log.actor_nome?.toLowerCase().includes(normalizedSearch) ||
        log.action?.toLowerCase().includes(normalizedSearch) ||
        log.entity?.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [logs, search]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-auditoria">
      <div className="flex flex-col gap-2">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Auditoria</h1>
        <p className="text-slate-600">Acompanhe eventos importantes de contas, vendas, campanhas e resgates.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Buscar na auditoria..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Nenhum evento encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Data</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Evento</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Usuário</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Entidade</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Descrição</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50/50">
                    <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                      {formatDateTime(log.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {formatAuditAction(log.action)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-slate-900">{log.actor_nome || "Sistema"}</p>
                      <p className="text-xs text-slate-500">{formatUserType(log.actor_tipo || "sistema")}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {formatAuditEntity(log.entity)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 max-w-md">
                      {log.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditoria;
